import copy from 'copy-to-clipboard';
import qs from 'qs';
import React, { useCallback, useMemo, useState } from 'react';
import { Box, Button, Card, CardContent, CardHeader, Grid, Tooltip } from '@mui/material';
import { Feature } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { QlfPlaceholder, colorPalette, sxs } from '@qualifio/ui-commons';
import { faAddressCard } from '@fortawesome/pro-solid-svg-icons/faAddressCard';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';
import { faGifts } from '@fortawesome/pro-solid-svg-icons/faGifts';
import { faIdCardClip } from '@fortawesome/pro-solid-svg-icons/faIdCardClip';
import { faLink } from '@fortawesome/pro-solid-svg-icons/faLink';
import { faReceipt } from '@fortawesome/pro-solid-svg-icons/faReceipt';
import { faShareAlt } from '@fortawesome/pro-solid-svg-icons/faShareAlt';
import { faUsdCircle } from '@fortawesome/pro-solid-svg-icons/faUsdCircle';
import { faUserCircle } from '@fortawesome/pro-solid-svg-icons/faUserCircle';
import { useTranslation } from 'react-i18next';
import { DetailForm } from '../../../shared/layout/DetailForm';
import { MemberBlockedAlert } from './MemberBlockedAlert';
import { OfferRewardButton } from './OfferRewardButton';
import { ProfileCurrencyList } from './ProfileCurrencyList';
import { ProfileDetailsIdentitiesList } from './ProfileDetailsIdentitiesList';
import { ProfileDetailsList } from './ProfileDetailsList';
import { ProfileDetailsMenu } from './ProfileDetailsMenu';
import { ProfileDetailsTitle } from './ProfileDetailsTitle';
import { ProfileDisplayName } from '../../utils/ProfileDisplayName';
import { ProfileInteractions } from './ProfileInteractions';
import { ProfileLevel } from './ProfileLevel';
import { ProfileRewards } from './ProfileRewards';
import { ProfileTransactions } from './ProfileTransactions';
import { QlfPlaceholderIcon } from '../../../shared/qlf-placeholder-icon/QlfPlaceholderIcon';
import { SxStyles } from '../../../shared/utils/types';
import { getProfileColor } from './get-profile-color';
import { getUserMarkers } from '../../../shared/markers/get-user-markers';
import { isInShell } from '../../../shell/shell';
import { levelsIcon } from '../../../shared/icons';
import { profilesPageSize, useProfiles } from '../../ProfileContext';
import { useFeatures } from '../../../features/FeaturesContext';
import { useProgram } from '../../../loyalty/ProgramContext';

const styles: SxStyles = {
  card: {
    backgroundColor: colorPalette.grey.light,
    borderTopWidth: 4,
    borderTopStyle: 'solid',
    marginBottom: 2,
  },
  cardHeader: {
    height: 66,
    borderBottom: `1px solid ${colorPalette.grey.main}`,
    padding: [2, 3],
  },
  cardTitle: {
    display: 'inline',
    margin: [0, 1],
    color: colorPalette.marine.main,
    '&.svg': {
      color: colorPalette.marine.main,
    },
  },
  cardContent: {
    padding: [2, 3],
  },
};

export function ProfileDetails(): React.ReactElement {
  const { t } = useTranslation();
  const [toolTipValue, setToolTipValue] = useState<string>(t('profiles.details.title.url.tooltip'));
  const {
    program: { id: programId },
  } = useProgram();
  const {
    profiles,
    setProfiles,
    selectedProfile,
    currentPage,
    refreshProfile,
    setSelectedProfile,
    goToPreviousPage,
    goToNextPage,
  } = useProfiles();
  const { features } = useFeatures();
  const { enabled: eventReportEnabled } = features[Feature.EVENT_REPORT];
  const { read: interactionRead } = features[Feature.INTERACTION];

  const membership = useMemo(
    () => selectedProfile?.memberships?.find(m => m.programId === programId),
    [selectedProfile, programId],
  );

  const currentIndex = selectedProfile ? profiles?.data.indexOf(selectedProfile) : -1;

  const canNext = currentIndex >= profiles?.data.length - 1 && profiles.isLast;
  const canPrevious = currentIndex <= 0 && currentPage === 0;

  const handleNext = () => {
    if (currentIndex + 1 === profilesPageSize) {
      goToNextPage();
    } else {
      const nextProfile = profiles.data[currentIndex + 1];
      setSelectedProfile(nextProfile);
    }
  };
  const handlePrevious = () => {
    if (currentPage > 0 && currentIndex === 0) {
      goToPreviousPage();
    } else {
      const previousProfile = profiles.data[currentIndex - 1];
      setSelectedProfile(previousProfile);
    }
  };

  const markers = useMemo(
    () =>
      selectedProfile ? getUserMarkers(selectedProfile, membership, { sx: { marginLeft: 1 } }) : [],
    [selectedProfile, membership],
  );

  const userColor = useMemo(
    () =>
      selectedProfile && membership
        ? getProfileColor(selectedProfile, membership) || colorPalette.blue.main
        : '',
    [selectedProfile, membership],
  );

  const resetTooltip = () => {
    setToolTipValue(t('profiles.details.title.url.tooltip'));
  };
  const copyAndSetTooltip = useCallback((text: string) => {
    copy(text);
    setToolTipValue(t('profiles.details.title.url.tooltip.copied'));
    setTimeout(resetTooltip, 2000);
  }, []);

  const profileId = selectedProfile?.id;
  const formatedQparams = profileId
    ? qs.stringify({ search: profileId }, { addQueryPrefix: true })
    : '';

  const profileUrl =
    window.parent.location.origin + window.parent.location.pathname + formatedQparams;

  const eventReportsLink = selectedProfile
    ? `${isInShell() ? '/s/loyalty' : '/loy'}/accounts/${
        selectedProfile.accountId
      }/event-reports?${qs.stringify({
        programIds: [programId],
        profileId: selectedProfile.id,
        isTestUser: selectedProfile.isTestUser,
      })}`
    : undefined;

  if (!selectedProfile) {
    return (
      <QlfPlaceholder
        icon={<QlfPlaceholderIcon icon={faAddressCard} />}
        primaryText={t('profiles.states.empty.title')}
        secondaryText={t('profiles.states.empty.description')}
      />
    );
  }

  if (!selectedProfile || !selectedProfile.memberships || !membership) {
    return (
      <QlfPlaceholder
        icon={<QlfPlaceholderIcon icon={faAddressCard} />}
        primaryText={t('profiles.states.not-member.title')}
        secondaryText={t('profiles.states.not-member.description')}
      />
    );
  }

  return (
    <DetailForm>
      <DetailForm.Form>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <ProfileDetailsTitle>
            <Box sx={{ alignItems: 'center', display: 'flex' }}>
              <Box>
                <ProfileDisplayName profile={selectedProfile} />
              </Box>
              {markers}
            </Box>
          </ProfileDetailsTitle>
          <Box sx={{ display: 'flex' }}>
            <Box mr={1}>
              <ProfileDetailsMenu
                membership={membership}
                setMembership={updatedMembership => {
                  const updatedMemberships = selectedProfile.memberships?.map(m =>
                    m.id === updatedMembership.id ? updatedMembership : m,
                  );
                  setSelectedProfile({
                    ...selectedProfile,
                    memberships: updatedMemberships,
                  });
                  const profile = profiles.data.find(profile => profile.id === selectedProfile.id);
                  if (profile) {
                    profile.memberships = updatedMemberships;
                  }
                  setProfiles(profiles);
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', marginBottom: 1 }}>
              <Button color="grey" onClick={handlePrevious} disabled={canPrevious}>
                <FontAwesomeIcon fontSize="inherit" icon={faChevronLeft} />
              </Button>
              <Button color="grey" onClick={handleNext} disabled={canNext} sx={{ marginLeft: 0.5 }}>
                <FontAwesomeIcon fontSize="inherit" icon={faChevronRight} />
              </Button>
            </Box>
          </Box>
        </Box>

        {membership?.blocked && (
          <MemberBlockedAlert
            profile={selectedProfile}
            programId={programId}
            onUnblock={refreshProfile}
          />
        )}

        <Grid container sx={{ gap: 'unset !important' }} spacing={2}>
          <Grid item xs={12} sm={6}>
            <Card sx={sxs(styles.card, { borderTopColor: userColor })}>
              <CardHeader
                sx={styles.cardHeader}
                titleTypographyProps={{
                  variant: 'h3',
                }}
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div>
                      <FontAwesomeIcon icon={faUserCircle} color={colorPalette.marine.main} />
                      <Box sx={styles.cardTitle}>{t('profiles.details.contact.title')}</Box>
                    </div>
                    <Tooltip title={toolTipValue} onClose={resetTooltip}>
                      <Box
                        component={FontAwesomeIcon}
                        icon={faLink}
                        size="xs"
                        sx={{ marginLeft: 0.5, cursor: 'pointer', color: colorPalette.marine.main }}
                        onClick={() => copyAndSetTooltip(profileUrl)}
                      />
                    </Tooltip>
                  </div>
                }
              />
              <CardContent sx={styles.cardContent}>
                <ProfileDetailsList profile={selectedProfile} />
              </CardContent>
            </Card>
            <Card sx={sxs(styles.card, { borderTopColor: userColor })}>
              <CardHeader
                sx={styles.cardHeader}
                titleTypographyProps={{
                  variant: 'h3',
                }}
                title={
                  <div>
                    <FontAwesomeIcon icon={faIdCardClip} color={colorPalette.marine.main} />
                    <Box sx={styles.cardTitle}>{t('profiles.details.identity.title')}</Box>
                  </div>
                }
              />
              <CardContent sx={styles.cardContent}>
                <ProfileDetailsIdentitiesList profile={selectedProfile} />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Card sx={sxs(styles.card, { borderTopColor: userColor })}>
              <CardHeader
                sx={styles.cardHeader}
                titleTypographyProps={{
                  variant: 'h3',
                }}
                title={
                  <div>
                    <FontAwesomeIcon icon={faUsdCircle} color={colorPalette.marine.main} />
                    <Box sx={styles.cardTitle}>{t('profiles.details.currencies.title')}</Box>
                  </div>
                }
              />
              <CardContent sx={styles.cardContent}>
                <ProfileCurrencyList profile={selectedProfile} onBalanceAdjusted={refreshProfile} />
              </CardContent>
            </Card>

            <Card sx={sxs(styles.card, { borderTopColor: userColor })}>
              <CardHeader
                sx={styles.cardHeader}
                titleTypographyProps={{
                  variant: 'h3',
                }}
                title={
                  <div>
                    <FontAwesomeIcon icon={levelsIcon} color={colorPalette.marine.main} />
                    <Box sx={styles.cardTitle}>{t('profiles.details.level.title')}</Box>
                  </div>
                }
              />
              <CardContent sx={styles.cardContent}>
                <ProfileLevel
                  profile={selectedProfile}
                  membership={membership}
                  onLevelAdjusted={refreshProfile}
                />
              </CardContent>
            </Card>

            <Card sx={sxs(styles.card, { borderTopColor: userColor })}>
              <CardHeader
                sx={styles.cardHeader}
                titleTypographyProps={{
                  variant: 'h3',
                }}
                title={
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span>
                      <FontAwesomeIcon icon={faGifts} color={colorPalette.marine.main} />
                      <Box sx={styles.cardTitle}>{t('profiles.details.rewards.title')}</Box>
                    </span>
                    <OfferRewardButton profile={selectedProfile} onOffer={refreshProfile} />
                  </Box>
                }
              />
              <CardContent sx={styles.cardContent}>
                <ProfileRewards />
              </CardContent>
            </Card>

            <Card sx={sxs(styles.card, { borderTopColor: userColor })}>
              <CardHeader
                sx={styles.cardHeader}
                titleTypographyProps={{
                  variant: 'h3',
                }}
                title={
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span>
                      <FontAwesomeIcon icon={faShareAlt} color={colorPalette.marine.main} />
                      <Box sx={styles.cardTitle}>{t('profiles.details.transactions.title')}</Box>
                    </span>
                    {eventReportEnabled && eventReportsLink ? (
                      <a
                        style={{ color: '#61c1db', textDecoration: 'none !important' }}
                        href={eventReportsLink}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {t('profiles.details.transactions.link')}
                      </a>
                    ) : (
                      ''
                    )}
                  </Box>
                }
              />
              <CardContent sx={styles.cardContent}>
                <ProfileTransactions />
              </CardContent>
            </Card>

            {interactionRead ? (
              <Card sx={sxs(styles.card, { borderTopColor: userColor })}>
                <CardHeader
                  sx={styles.cardHeader}
                  titleTypographyProps={{
                    variant: 'h3',
                  }}
                  title={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span>
                        <FontAwesomeIcon icon={faReceipt} color={colorPalette.marine.main} />
                        <Box sx={styles.cardTitle}>{t('profiles.details.interactions.title')}</Box>
                      </span>
                    </Box>
                  }
                />
                <CardContent sx={styles.cardContent}>
                  <ProfileInteractions />
                </CardContent>
              </Card>
            ) : null}
          </Grid>
        </Grid>
      </DetailForm.Form>
    </DetailForm>
  );
}
